/**
 * ## cortex:std:color
 * Exposes an api that you can use to modify brightness of a given hex or check
 * if the hex is a dark or light color (based on a YIQ equation)
 */
export namespace Color {
	// Only used some functionality from https://github.com/Qix-/color/blob/master/index.js
	// since they don't seem to support tree shaking and we're only interested in
	// a few lines from that lib anyway

	type RgbTuple = [number, number, number, number];
	type Hex = string;

	const HEX_REGEX = /^#([a-f0-9]{6})([a-f0-9]{2})?$/i;

	/**
	 * Changes the brightness by an amount
	 *
	 * @param hex a valid color code, e.g. #1ea0b4
	 * @param ratio a int value
	 */
	export function lightLevel(hex: Hex, amount: number): string {
		// To visualize how the color changes in a color picker, here's how a
		// positive amount effects a color:
		// Before running this function, RGB values are: [23, 95, 45] (hex #175F2D)
		// -  -  -  -  -
		// -  -  -  -  -
		// -  -  -  -  -
		// -  -  -  x  -
		// -  -  -  -  -
		// After running this function with a amount of 40, the new RGB values
		// are now: [63, 135, 75] (hex #357D41)
		// -  -  -  -  -
		// -  -  -  -  -
		// -  -  x  -  -
		// -  -  -  -  -
		// -  -  -  -  -
		// By adding another 40, it'll move linear closer to the top left corner.
		// Having a negative amount will move it linear in the other direction

		/**
		 * Scoped helper to deal with overflows
		 */
		function normalizeValue(value: number): number {
			if (value > 255) {
				return 255;
			}

			if (value < 0) {
				return 0;
			}

			return value;
		}

		const rgb = createRgb(hex);

		const r = normalizeValue(rgb[0] + amount);
		const g = normalizeValue(rgb[1] + amount);
		const b = normalizeValue(rgb[2] + amount);

		return createHex([r, g, b, 1]);
	}

	/**
	 * Converts a color code to an RGB tuple
	 *
	 * @param hex a valid color code, e.g. #1ea0b4
	 */
	export function createRgb(hex: Hex): RgbTuple {
		if (!HEX_REGEX.test(hex)) {
			throw new Error('Tried to modify a non hex string');
		}

		// Hex is base 16
		const num = parseInt(hex.slice(1), 16);

		const red = num >> 16;
		const green = (num >> 8) & 0x00ff;
		const blue = num & 0x0000ff;

		// The last number in the tuple is alpha, and it's not currently implemented
		return [red, green, blue, 1];
	}

	/**
	 * Converts an RGB tuple to a color code
	 *
	 * @param rgb an tuple containing hex decimal values from 0 - 255
	 */
	export function createHex(rgb: RgbTuple): string {
		function section(num: number): string {
			const str = num.toString(16).toLowerCase();

			return str.length < 2 ? `0${str}` : str;
		}

		return `#${section(rgb[0])}${section(rgb[1])}${section(rgb[2])}`;
	}

	/**
	 * Checks if a color is "dark" based on a YIQ equation
	 * The one being used can be found here:
	 * http://24ways.org/2010/calculating-color-contrast
	 *
	 * @param hex a valid color code, e.g. #1ea0b4
	 */
	export function isDark(hex: Hex): boolean {
		// YIQ equation from http://24ways.org/2010/calculating-color-contrast
		const rgb = createRgb(hex);
		const yiq = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000;

		return yiq < 128;
	}

	/**
	 * Checks if a color is "light" based on a YIQ equation.
	 * The one being used can be found here:
	 * http://24ways.org/2010/calculating-color-contrast
	 *
	 * @param hex a valid color code, e.g. #1ea0b4
	 */
	export function isLight(hex: Hex): boolean {
		return !isDark(hex);
	}
}
